import { Directive, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ConfigSidebarService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { IMapTable } from '../../../interfaces/mapTable.interface';
import { safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';

@Directive()
export abstract class BaseTableComponent<T> implements OnInit, OnChanges {
  @Input() public config: IMapTable = null;

  public dataGrid: T[] = [];
  public columnsGrid: MtxGridColumn[] = [];
  public pageSize = 10;
  public pageSizeOptions = [10, 50, 100, 500];
  public currentPageIndex = 0;
  public offset = 0;
  public totalCount = 0;

  protected _exportFilename = 'export';
  protected _currentSort: { field: string; direction: number };
  protected _exportApi;

  //service
  protected _configSidebarService = inject(ConfigSidebarService);
  protected _ebwModal = inject(EbwModalService);
  protected _translateService = inject(TranslateService);
  protected _utilsService = inject(UtilsService);

  //
  protected abstract fetchData(customFilter?: Record<string, any>): void;
  protected abstract initializeTable(): void;
  protected abstract getFilters(limit?: number, offset?: number): Record<string, any>;
  protected abstract formatExportData(data: Record<string, any>[], excel: boolean);

  ngOnInit(): void {
    this.initializeTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.init(0);
  }
  protected init(offset?: number) {

    
    this._currentSort = null;

    let filters = {};
    // se l'offset è valorizzato allora resetto effettivamente i valori
    if (!isNaN(offset)) {
      this.currentPageIndex = 0;
      this.offset = offset;
      filters['offset'] = offset;
    }
    this.fetchData(filters);
  }

  public onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPageIndex = event.pageIndex;
    this.offset = event.pageIndex * this.pageSize;

    this.fetchData();
  }

  public onSortChange(event: Sort) {
    if (event.direction !== '') {
      this._currentSort = {
        field: event.active,
        direction: event.direction === 'asc' ? -1 : 1
      };
    } else {
      this._currentSort = null;
    }
    this.fetchData();
  }

  public resetComponentProperties() {
    this.dataGrid = [];
  }

  async onExport(type: string) {
    let url, body, blobType;

    let dataUrl = this.config.url;
    // Export delle sole righe che vedo
    // Per l'export massivo devono richiedere una CR per gestire tutto il giro completo con RabbitMQ
    let filters = this.getFilters();

    try {
      const data: any = await firstValueFrom(
        this._utilsService.postRequest(dataUrl, filters, false, {}, 'application/json')
      );

      if (!data) {
        this._ebwModal.openErrorModal({
          title: this._translateService.instant('common.error'),
          content: ''
        });
        return;
      }

      // data.records.forEach((record) => this._formatCollectionData(record));
      // setup the blobType, url and body depending on the type of file to export
      switch (type) {
        case 'csv':
          url = this._exportApi.csv ? this._exportApi.csv + '?filename=' + this._exportFilename : null;
          body = { data: this.formatExportData(data.records, false) };
          blobType = 'text/csv';
          break;

        case 'html':
          url = this._exportApi.html ? this._exportApi.html + '?filename=' + this._exportFilename : null;
          body = { data: this.formatExportData(data.records, false) };
          blobType = 'text/html';
          break;
      }

      const opt = {
        responseType: 'blob'
      };

      this._utilsService.postRequest(url, body, false, opt, 'application/json').subscribe({
        next: (data: any) => {
          let downloadLink = document.createElement('a');
          let timestamp = new Date().toISOString();
          downloadLink.href = window.URL.createObjectURL(new Blob([data], { type: blobType }));
          downloadLink.setAttribute('download', `${this._exportFilename}-${timestamp}.${type}`);

          document.body.appendChild(downloadLink);
          downloadLink.click();
        },
        error: (error: any) => {
          this._ebwModal.openErrorModal({
            title: this._translateService.instant('common.error'),
            content: safeJsonStringify(error, String(error))
          });
        }
      });
    } catch (error) {
      this._ebwModal.openErrorModal({
        title: this._translateService.instant('common.error'),
        content: safeJsonStringify(error?.message || this._translateService.instant('common.data_retrieving_error'), '')
      });
    }
  }

  protected defaultFormatData(data: any[], excel: boolean = false) {
    const formattedData = [];

    for (const row of data) {
      const currentRow = {};
      for (const col of this.columnsGrid) {
        if (typeof row[col.field] === 'object' && !Array.isArray(row[col.field]) && row[col.field] !== null) {
          currentRow[<string>col.header] = `${row[col.field].latitude} ${row[col.field].longitude}`;
          continue;
        }

        if (!row[col.field]) {
          currentRow[<string>col.header] = '';
          continue;
        }

        currentRow[<string>col.header] = row[col.field];
      }
      formattedData.push(currentRow);
    }

    if (excel) {
      return {
        query: formattedData
      };
    }

    return formattedData;
  }
}
